import { useQuery } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { post } from "common/helpers/HTTP";
import { parseLeadsSearchParams } from "common/helpers/utils";
import type {
  FacetParam,
  LeadsFacets,
  LeadsSearchParams,
  SelectedFacetParam,
} from "common/types";

async function getLeadsFacetRequest(
  workspaceId: string,
  facetName: keyof LeadsFacets,
  searchParams: LeadsSearchParams<SelectedFacetParam>,
): Promise<FacetParam[]> {
  const data = await post(
    `workspaces/${workspaceId}/leads-search-facets/${facetName}`,
    parseLeadsSearchParams(searchParams),
  );

  const facetData = data[facetName] as FacetParam[];

  // We merge location facets that have the same name but different IDs
  if (facetName === "locations") {
    const locationMap = new Map<string, FacetParam>();

    facetData.forEach((location) => {
      if (locationMap.has(location.name)) {
        const existing = locationMap.get(location.name);
        locationMap.set(location.name, {
          ...existing,
          target_count: existing.target_count + location.target_count,
          id: `${existing.id},${location.id}`,
        });
      } else {
        locationMap.set(location.name, location);
      }
    });
    return Array.from(locationMap.values());
  }
  return facetData;
}

export default function useLeadsFacet(
  facetName: keyof LeadsFacets,
  searchParams: LeadsSearchParams<SelectedFacetParam>,
) {
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { leadsKeys } = getQueryKeys(workspaceId);

  const {
    data: facetData,
    isFetching: isFetchingFacet,
    error: facetError,
    refetch: refetchFacet,
  } = useQuery({
    queryKey: leadsKeys.facet(facetName, searchParams),
    queryFn: () => getLeadsFacetRequest(workspaceId, facetName, searchParams),
    refetchOnMount: false,
    placeholderData: (prev) => prev,
  });

  return {
    facetData,
    isFetchingFacet,
    facetError,
    refetchFacet,
  };
}
